import { useRouter } from '@react-aria/utils';
import { type ForwardedRef, useCallback, useRef } from 'react';
import { RouterProvider } from '../../providers/router/router-provider.js';
import { IconLinkButton } from '../button/icon-link-button.js';
import { LinkButton } from '../button/link-button.js';
import type { FocusableElement, Href, NavigationProps, RouterOptions } from '../types.js';
import { BasePagination, type BasePaginationProps } from './base-pagination.js';

interface PaginationRouterOptions {
  __INTERNAL__CIMPRESS__UI__?: {
    paginationPrevious?: boolean;
    paginationNext?: boolean;
  };
}

/** @internal */
export interface LinkPaginationProps extends BasePaginationProps, Omit<NavigationProps, 'href'> {
  navRef?: ForwardedRef<HTMLElement>;
  /** A callback function that is called when the page number changes. */
  onPageChange?: (page: number) => void;
  /** A function that returns a URL for a given page number. */
  href: (page: number) => string;
}

/** @internal */
export function LinkPagination({
  currentPage,
  pageCount,
  href,
  onPageChange,

  // Navigation props
  hrefLang,
  target,
  download,
  ping,
  referrerPolicy,
  rel,
  routerOptions,

  ...props
}: LinkPaginationProps) {
  const router = useRouter();
  const listRef = useRef<HTMLUListElement>(null);

  const navigationProps = {
    hrefLang,
    target,
    download,
    ping,
    referrerPolicy,
    rel,
    routerOptions,
  };

  const navigationInterceptor = useCallback(
    (path: Href, routerOptions?: RouterOptions) => {
      // If navigation would cause the activated link to become disabled,
      // we have to move focus to a different link beforehand.

      if (!listRef.current) {
        return;
      }

      const { paginationPrevious, paginationNext } =
        (routerOptions as PaginationRouterOptions | undefined)?.__INTERNAL__CIMPRESS__UI__ ?? {};

      const element = listRef.current.querySelector(
        `[href='${path}']${paginationPrevious ? '[data-previous]' : ':not([data-previous])'}${paginationNext ? '[data-next]' : ':not([data-next])'}`,
      )!;

      if (paginationPrevious && currentPage === 2) {
        // "Previous" link will become disabled, so move focus to the first page link
        (listRef.current?.querySelector(`[href][data-first]`) as FocusableElement | undefined)?.focus();
      } else if (paginationNext && currentPage === pageCount - 1) {
        // "Next" link will become disabled, so move focus to the last page link
        (listRef.current?.querySelector(`[href][data-last]`) as FocusableElement | undefined)?.focus();
      }

      onPageChange?.(Number((element as HTMLElement).dataset.page));

      // We don't have access to the modifiers of the original event.
      // However, this function wouldn't run if the original modifiers impacted routing, so it is safe to pass an empty object.
      router.open(element, {}, path, routerOptions);
    },
    [router, currentPage, pageCount, onPageChange],
  );

  return (
    <RouterProvider navigate={navigationInterceptor}>
      <BasePagination
        {...props}
        listRef={listRef}
        currentPage={currentPage}
        pageCount={pageCount}
        renderPreviousButton={(previousButtonProps) => (
          <IconLinkButton
            {...navigationProps}
            {...previousButtonProps}
            href={href(currentPage - 1)}
            routerOptions={
              {
                ...(navigationProps.routerOptions ?? {}),
                __INTERNAL__CIMPRESS__UI__: {
                  paginationPrevious: true,
                },
              } as PaginationRouterOptions as RouterOptions
            }
            data-previous
            data-page={currentPage - 1}
          />
        )}
        renderNextButton={(nextButtonProps) => (
          <IconLinkButton
            {...navigationProps}
            {...nextButtonProps}
            href={href(currentPage + 1)}
            routerOptions={
              {
                ...(navigationProps.routerOptions ?? {}),
                __INTERNAL__CIMPRESS__UI__: {
                  paginationNext: true,
                },
              } as PaginationRouterOptions as RouterOptions
            }
            data-next
            data-page={currentPage + 1}
          />
        )}
        renderPageButton={({ pageNumber, isCurrentPage, pageButtonProps }) => (
          <LinkButton
            {...navigationProps}
            {...pageButtonProps}
            aria-current={isCurrentPage ? 'page' : undefined}
            href={href(pageNumber)}
            data-page={pageNumber}
          />
        )}
      />
    </RouterProvider>
  );
}
